import { clsx, type ClassValue } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * The type roles in `src/index.css`. Each sets the face, size, leading and
 * weight at once, so a role must replace those classes rather than sit beside
 * them and let the cascade pick.
 */
const TYPE_ROLES = [
  'page-title',
  'section-title',
  'empty-title',
  'eyebrow',
  'stat-label',
  'lede',
  'figure'
]

const twMerge = extendTailwindMerge<'type-role'>({
  extend: {
    classGroups: { 'type-role': [{ type: TYPE_ROLES }] },
    conflictingClassGroups: {
      'type-role': [
        'font-size',
        'font-weight',
        'font-family',
        'leading',
        'tracking'
      ]
    }
  }
})

/**
 * Compose Tailwind classes, resolving conflicts in favour of the last one.
 *
 * `clsx` handles conditionals; `twMerge` is what makes a caller's `px-6` beat
 * a component's own `px-4` instead of both landing in the class list and the
 * cascade deciding at random. A type role such as `type-section-title` beats
 * an earlier `text-lg` or `font-semibold` the same way.
 *
 * @param inputs - Class values, conditional or plain.
 * @returns The merged class string.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}
